import { Input } from '@monorepo/components'
import { Field, Formik, setNestedObjectValues } from 'formik'
import Select from 'react-select'
import React from 'react'
import * as S from './styles'
import { ChannelHelper, DealerHelper, isNonEmptyString } from '@monorepo/infra'
import { IDealerForm, IReactSelectOption } from '@monorepo/interfaces'

type ManualAddDealerAdminFormProps = {
  close: () => void
  actionForm: (data: IDealerForm) => void
  error: string
}

interface ManualAddFormValues {
  firstName: string
  lastName: string
  email: string
  masterDealerId: string
  channels: IReactSelectOption[]
}

// Role is always Admin for the manual-add flow — Principals come from the
// dealer file and Members are added by Admins, so neither is selectable here.
const dealerRoleAdminOnly: IReactSelectOption[] = [
  { value: '/dealerAdmin', label: 'Admin' },
]

const channelOptions = ChannelHelper.getChannelOptions()

const initialValues: ManualAddFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  masterDealerId: '',
  channels: [],
}

const ManualAddDealerAdminForm: React.FC<ManualAddDealerAdminFormProps> = ({
  actionForm,
  close,
  error,
}) => {
  return (
    <>
      {isNonEmptyString(error) && (
        <S.ErrorMessage>{error.trim()}</S.ErrorMessage>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={DealerHelper.ManualAddDealerAdminSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values, actions) => {
          actions.setSubmitting(false)
          const valuesParsed: IDealerForm = {
            firstName: values.firstName?.trim(),
            lastName: values.lastName?.trim(),
            email: values.email?.trim()?.toLowerCase(),
            masterDealerId: values.masterDealerId?.trim(),
            channels: (values.channels || []).map((data) => data.value),
            groups: ['/dealerAdmin'],
          }
          actionForm(valuesParsed)
        }}
      >
        {({
          errors,
          touched,
          values,
          setFieldValue,
          setFieldTouched,
          validateForm,
          submitForm,
          setTouched,
        }) => (
          <S.DialogForm>
            <Field
              id="masterDealerId"
              name="masterDealerId"
              component={Input}
              value={values.masterDealerId}
              invalid={touched.masterDealerId && errors.masterDealerId}
              invalidMessage={errors.masterDealerId}
              label="Master Dealer ID"
              onBlur={() => setFieldTouched('masterDealerId')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('masterDealerId', option.target.value)
              }}
            />

            <Field
              id="firstName"
              name="firstName"
              component={Input}
              value={values.firstName}
              invalid={touched.firstName && errors.firstName}
              invalidMessage={errors.firstName}
              label="First name"
              onBlur={() => setFieldTouched('firstName')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('firstName', option.target.value)
              }}
            />

            <Field
              id="lastName"
              name="lastName"
              component={Input}
              value={values.lastName}
              invalid={touched.lastName && errors.lastName}
              invalidMessage={errors.lastName}
              label="Last name"
              onBlur={() => setFieldTouched('lastName')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('lastName', option.target.value)
              }}
            />

            <Field
              id="email"
              name="email"
              component={Input}
              value={values.email}
              invalid={touched.email && errors.email}
              invalidMessage={errors.email}
              label="Email Address"
              onBlur={() => setFieldTouched('email')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('email', option.target.value)
              }}
            />

            <div>
              <Select
                name="channels"
                placeholder="Channel(s)"
                isMulti
                options={channelOptions}
                value={values.channels}
                onChange={(newValue) => setFieldValue('channels', newValue)}
                onBlur={() => setFieldTouched('channels')}
                components={{
                  IndicatorSeparator: () => null,
                }}
                isClearable={true}
                styles={S.MultiSelectStyle}
              />
              {touched.channels && errors.channels && (
                <S.FieldError>{errors.channels as string}</S.FieldError>
              )}
            </div>

            <Select
              name="role"
              placeholder="Dealer Role"
              options={dealerRoleAdminOnly}
              components={{
                IndicatorSeparator: () => null,
              }}
              isSearchable={false}
              isDisabled={true}
              value={dealerRoleAdminOnly[0]}
              styles={S.MultiSelectStyle}
            />

            <S.DialogActions>
              <S.ModalButton
                colorOption="stroke"
                label="CANCEL"
                onClick={() => close()}
              />
              <S.ModalButton
                colorOption="black"
                label="SAVE"
                onClick={() => {
                  validateForm().then((errors) => {
                    setTouched(setNestedObjectValues(errors, true))
                  })
                  submitForm()
                }}
              />
            </S.DialogActions>
          </S.DialogForm>
        )}
      </Formik>
    </>
  )
}

export default ManualAddDealerAdminForm
